import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchVendorDetail,
  clearVendorDetail,
  createManualPayout,
} from "../../../slice/vendor/vendorSlice";

const fmtSGD = (n) => "$" + Math.round(n || 0).toLocaleString("en-SG");
const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-SG", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const StatCard = ({ label, value }) => (
  <div style={{
    background: "#fff", borderRadius: 16, padding: "18px 20px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)", flex: 1, minWidth: 160,
  }}>
    <div style={{ fontSize: 12, fontWeight: 600, color: "#9ca3af", marginBottom: 6 }}>{label}</div>
    <div style={{ fontSize: 20, fontWeight: 800, color: "#111827" }}>{value}</div>
  </div>
);

const StatusPill = ({ status }) => {
  const isPaid = status === "paid";
  return (
    <span style={{
      display: "inline-block", padding: "4px 12px", borderRadius: 999,
      fontSize: 12, fontWeight: 700,
      color: isPaid ? "#059669" : "#b45309",
      background: isPaid ? "#ecfdf5" : "#fffbeb",
    }}>
      {isPaid ? "Paid" : "Pending"}
    </span>
  );
};

const ManualPaidModal = ({ availableBalance, onClose, onSubmit, submitting, errorMsg }) => {
  const [amount, setAmount] = useState("");

  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(17,24,39,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 16, padding: 24, width: 360,
          boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
        }}
      >
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "#111827" }}>
          Manual Paid
        </h3>
        <p style={{ margin: "6px 0 16px", fontSize: 13, color: "#9ca3af" }}>
          Available balance: {fmtSGD(availableBalance)}
        </p>

        <input
          type="number"
          min="0"
          step="0.01"
          placeholder="Enter amount"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          style={{
            width: "100%", padding: "10px 14px", borderRadius: 10,
            border: "1px solid #e5e7eb", fontSize: 14, outline: "none",
            boxSizing: "border-box", marginBottom: 8,
          }}
        />

        {errorMsg && (
          <div style={{ color: "#dc2626", fontSize: 13, marginBottom: 8 }}>
            {errorMsg}
          </div>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              flex: 1, padding: "10px 0", borderRadius: 10, border: "1px solid #e5e7eb",
              background: "#fff", color: "#374151", fontWeight: 700, cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={submitting || !amount}
            onClick={() => onSubmit(Number(amount))}
            style={{
              flex: 1, padding: "10px 0", borderRadius: 10, border: "none",
              background: submitting || !amount ? "#a7f3d0" : "#059669",
              color: "#fff", fontWeight: 700,
              cursor: submitting || !amount ? "not-allowed" : "pointer",
            }}
          >
            {submitting ? "Saving..." : "Paid"}
          </button>
        </div>
      </div>
    </div>
  );
};

const VendorReportDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { detail, detailLoading, detailError } = useSelector((s) => s.vendorReport);
  const [tab, setTab] = useState("revenue");
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [modalError, setModalError] = useState("");

  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => {
    if (id) dispatch(fetchVendorDetail(id));
    return () => dispatch(clearVendorDetail());
  }, [dispatch, id]);

  useEffect(() => {
    setSearch("");
  }, [tab]);

  const handleManualPaid = async (amount) => {
    if (!amount || amount <= 0) {
      setModalError("Enter a valid amount");
      return;
    }
    setSubmitting(true);
    setModalError("");
    try {
      await dispatch(createManualPayout({ vendorId: id, amount })).unwrap();
      setShowModal(false);
      dispatch(fetchVendorDetail(id));
    } catch (err) {
      setModalError(err || "Failed to add manual payout");
    } finally {
      setSubmitting(false);
    }
  };

  const orders = detail?.orders || [];
  const settlements = detail?.settlements || [];

  const dateBounds = useMemo(() => {
    const fromDate = dateFrom ? new Date(dateFrom + "T00:00:00") : null;
    const toDate = dateTo ? new Date(dateTo + "T23:59:59.999") : null;
    return { fromDate, toDate };
  }, [dateFrom, dateTo]);

  const inRange = (dateVal, fromDate, toDate) => {
    if (!fromDate && !toDate) return true;
    const d = dateVal ? new Date(dateVal) : null;
    if (!d || isNaN(d.getTime())) return false;
    if (fromDate && d < fromDate) return false;
    if (toDate && d > toDate) return false;
    return true;
  };

  // Table rows: search + date, scoped to the active tab
  const filteredOrders = useMemo(() => {
    const q = search.trim().toLowerCase();
    const { fromDate, toDate } = dateBounds;
    return orders.filter((o) => {
      if (q && !(o.orderId || "").toLowerCase().includes(q)) return false;
      return inRange(o.date, fromDate, toDate);
    });
  }, [orders, search, dateBounds]);

  const filteredSettlements = useMemo(() => {
    const q = search.trim().toLowerCase();
    const { fromDate, toDate } = dateBounds;
    return settlements.filter((s) => {
      if (q && !(s.payoutId || "").toLowerCase().includes(q)) return false;
      return inRange(s.requestedDate, fromDate, toDate);
    });
  }, [settlements, search, dateBounds]);

  // Stat cards: date-only (ignores search box, ignores which tab is open)
  // so cards stay consistent no matter what the search field says.
  const dateOnlyOrders = useMemo(() => {
    const { fromDate, toDate } = dateBounds;
    return orders.filter((o) => inRange(o.date, fromDate, toDate));
  }, [orders, dateBounds]);

  const dateOnlySettlements = useMemo(() => {
    const { fromDate, toDate } = dateBounds;
    return settlements.filter((s) => inRange(s.requestedDate, fromDate, toDate));
  }, [settlements, dateBounds]);

  const isDateFiltered = Boolean(dateFrom || dateTo);

  const computedStats = useMemo(() => {
    if (!isDateFiltered || !detail) return detail?.stats;

    const totalRevenue = dateOnlyOrders.reduce((sum, o) => sum + (o.revenue || 0), 0);

    const totalPaid = dateOnlySettlements
      .filter((s) => s.status === "paid")
      .reduce((sum, s) => sum + (s.amount || 0), 0);

    const pendingPayout = dateOnlySettlements
      .filter((s) => s.status === "pending")
      .reduce((sum, s) => sum + (s.amount || 0), 0);

    const availableBalance = Math.max(totalRevenue - totalPaid, 0);

    return {
      totalRevenue,
      availableBalance,
      pendingPayout,
      totalPaid,
      deliveredOrders: dateOnlyOrders.length,
    };
  }, [isDateFiltered, detail, dateOnlyOrders, dateOnlySettlements]);

  const clearAllFilters = () => {
    setSearch("");
    setDateFrom("");
    setDateTo("");
  };

  const hasActiveFilters = search.trim() !== "" || dateFrom || dateTo;

  if (detailLoading) {
    return <div style={{ textAlign: "center", padding: "60px 0", color: "#9ca3af" }}>Loading vendor...</div>;
  }
  if (detailError) {
    return <div style={{ textAlign: "center", padding: "60px 0", color: "#dc2626" }}>{detailError}</div>;
  }
  if (!detail) return null;

  const { name } = detail;
  const stats = computedStats || detail.stats;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, paddingTop: "10px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            type="button"
            onClick={() => navigate("/admin/VendorReports")}
            style={{
              background: "#fff", border: "1px solid #eef0f3", borderRadius: 10,
              padding: "8px 10px", cursor: "pointer", display: "flex", alignItems: "center",
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: "#111827", margin: 0 }}>{name}</h1>
        </div>

        <button
          type="button"
          onClick={() => { setModalError(""); setShowModal(true); }}
          style={{
            padding: "10px 18px", borderRadius: 10, border: "none",
            background: "#111827", color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer",
          }}
        >
          Manual Paid
        </button>
      </div>

      {isDateFiltered && (
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "6px 12px", borderRadius: 20, background: "#eef2ff",
          color: "#4338ca", fontSize: 12, fontWeight: 700, marginBottom: 12,
        }}>
          📅 Stats shown for {dateFrom ? fmtDate(dateFrom) : "Any"} → {dateTo ? fmtDate(dateTo) : "Any"}
        </div>
      )}

      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 24 }}>
        <StatCard label="Total Revenue" value={fmtSGD(stats.totalRevenue)} />
        <StatCard label="Available Balance" value={fmtSGD(stats.availableBalance)} />
        <StatCard label="Pending Payout" value={fmtSGD(stats.pendingPayout)} />
        <StatCard label="Total Paid" value={fmtSGD(stats.totalPaid)} />
        <StatCard label="Delivered Orders" value={stats.deliveredOrders} />
      </div>

      {/* ── TABS + INLINE FILTERS ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: 16,
          borderBottom: "1px solid #eef0f3",
        }}
      >
        <div style={{ display: "flex", gap: 4 }}>
          {["revenue", "settlement"].map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              style={{
                background: "none", border: "none", cursor: "pointer",
                padding: "10px 18px", fontSize: 14, fontWeight: 700,
                color: tab === t ? "#111827" : "#9ca3af",
                borderBottom: tab === t ? "2px solid #111827" : "2px solid transparent",
                marginBottom: -1,
              }}
            >
              {t === "revenue" ? "Revenue" : "Settlement"}
            </button>
          ))}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10, paddingBottom: 10, flexWrap: "wrap" }}>
          {/* Search */}
          <div style={{ position: "relative" }}>
            <svg
              width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)" }}
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              type="text"
              placeholder={tab === "revenue" ? "Search order ID..." : "Search payout ID..."}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: 220,
                padding: "8px 12px 8px 32px",
                borderRadius: 9,
                border: "1px solid #e5e7eb",
                fontSize: 13,
                outline: "none",
                background: "#f9fafb",
                boxSizing: "border-box",
              }}
            />
          </div>

          {/* Date range */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              background: "#f9fafb",
              border: "1px solid #e5e7eb",
              borderRadius: 9,
              padding: "5px 10px",
            }}
          >
            <input
              type="date"
              value={dateFrom}
              max={dateTo || undefined}
              onChange={(e) => setDateFrom(e.target.value)}
              style={{
                border: "none", background: "transparent", fontSize: 13,
                color: dateFrom ? "#111827" : "#9ca3af", outline: "none",
                padding: "3px 2px", fontWeight: 600,
              }}
            />
            <span style={{ color: "#d1d5db", fontSize: 12 }}>→</span>
            <input
              type="date"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(e) => setDateTo(e.target.value)}
              style={{
                border: "none", background: "transparent", fontSize: 13,
                color: dateTo ? "#111827" : "#9ca3af", outline: "none",
                padding: "3px 2px", fontWeight: 600,
              }}
            />
          </div>

          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearAllFilters}
              style={{
                padding: "8px 14px",
                borderRadius: 9,
                border: "1px solid #fecaca",
                background: "#fef2f2",
                color: "#dc2626",
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      <div style={{ background: "#fff", borderRadius: 16, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
        {tab === "revenue" ? (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#fafbfc", borderBottom: "1px solid #eef0f3" }}>
                {["ORDER", "DATE", "ITEMS", "REVENUE"].map((h) => (
                  <th key={h} style={{
                    textAlign: "left", padding: "14px 20px", fontSize: 11,
                    fontWeight: 700, color: "#9ca3af", letterSpacing: 0.5,
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ padding: "40px 20px", textAlign: "center", color: "#9ca3af" }}>
                    {orders.length === 0 ? "No delivered orders yet." : "No orders match your filters."}
                  </td>
                </tr>
              ) : (
                filteredOrders.map((o, idx) => (
                  <tr key={o.orderId} style={{ borderBottom: idx < filteredOrders.length - 1 ? "1px solid #f3f4f6" : "none" }}>
                    <td style={{ padding: "16px 20px", fontSize: 14, fontWeight: 700, color: "#1f2937" }}>
                      {o.orderId}
                    </td>
                    <td style={{ padding: "16px 20px", fontSize: 14, color: "#374151" }}>
                      {fmtDate(o.date)}
                    </td>
                    <td style={{ padding: "16px 20px", fontSize: 14, color: "#374151" }}>
                      {o.itemsCount}
                    </td>
                    <td style={{ padding: "16px 20px", fontSize: 14, fontWeight: 700, color: "#059669" }}>
                      {fmtSGD(o.revenue)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#fafbfc", borderBottom: "1px solid #eef0f3" }}>
                {["PAYOUT ID", "REQUESTED", "AMOUNT", "SETTLED", "STATUS"].map((h) => (
                  <th key={h} style={{
                    textAlign: "left", padding: "14px 20px", fontSize: 11,
                    fontWeight: 700, color: "#9ca3af", letterSpacing: 0.5,
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredSettlements.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ padding: "40px 20px", textAlign: "center", color: "#9ca3af" }}>
                    {settlements.length === 0 ? "No settlements yet." : "No settlements match your filters."}
                  </td>
                </tr>
              ) : (
                filteredSettlements.map((s, idx) => (
                  <tr key={s.payoutId} style={{ borderBottom: idx < filteredSettlements.length - 1 ? "1px solid #f3f4f6" : "none" }}>
                    <td style={{ padding: "16px 20px", fontSize: 14, fontWeight: 700, color: "#1f2937" }}>
                      {s.payoutId}
                    </td>
                    <td style={{ padding: "16px 20px", fontSize: 14, color: "#374151" }}>
                      {fmtDate(s.requestedDate)}
                    </td>
                    <td style={{ padding: "16px 20px", fontSize: 14, fontWeight: 700, color: "#111827" }}>
                      {fmtSGD(s.amount)}
                    </td>
                    <td style={{ padding: "16px 20px", fontSize: 14, color: "#374151" }}>
                      {fmtDate(s.settledDate)}
                    </td>
                    <td style={{ padding: "16px 20px" }}>
                      <StatusPill status={s.status} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <ManualPaidModal
          availableBalance={stats.availableBalance}
          onClose={() => setShowModal(false)}
          onSubmit={handleManualPaid}
          submitting={submitting}
          errorMsg={modalError}
        />
      )}
    </div>
  );
};

export default VendorReportDetail;